import type { FC } from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Change } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { NoData } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import { LoaderContainer, LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import DurationService from "app/services/DurationService";
import { translate } from "app/filters/i18nFilter";
import { changeFilter, minVisitsAbbrFilter } from "app/filters/ngFilters";
import {
    POPChangeValueStyled,
    POPVisitsItemBar,
} from "app/pages/industry-analysis/overview/components/TotalVisitsChart/TotalVisitsChart.styles";
import { adaptPOPBarChartData } from "app/pages/industry-analysis/overview/components/TotalVisitsChart/utils/TotalVisitsDataAdapter";

import type { ITotalVisits } from "../../../../types";

import {
    TotalVisitsWrapper,
    Title,
    SidesWrapper,
    LeftBarContainer,
    BarContainer,
    DisplayValue,
    RightBarContainer,
    NoDataContainer,
    Period,
} from "./styledComponents";

interface ITotalVisitsProps {
    data: ITotalVisits[];
    isLoading?: boolean;
    showChange?: boolean;
}

const TotalVisits: FC<ITotalVisitsProps> = ({ data, isLoading, showChange = true }) => {
    const { params } = useCurrentStateAndParams();
    const { duration, comparedDuration } = params;
    const durations = DurationService.getDurationData(duration, comparedDuration);

    const metricNameProps = {
        comparedPeriod: {
            displayName: (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                >
                    {durations.forWidget[1]}
                </Flex>
            ),
        },
        currPeriod: {
            displayName: (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                >
                    {durations.forWidget[0]}
                </Flex>
            ),
        },
    };

    const isNotEmpty = data && data.length !== 0 && data.every((item) => item.Value);

    const periodOverPeriodData = useMemo(() => {
        return adaptPOPBarChartData(data);
    }, [data]);

    const valueProps = {
        comparedPeriod: {
            displayValue: minVisitsAbbrFilter()(periodOverPeriodData.comparedPeriod.totalVisits),
        },
        currPeriod: {
            displayValue: minVisitsAbbrFilter()(periodOverPeriodData.currPeriod.totalVisits),
        },
    };

    return (
        <TotalVisitsWrapper data-automation="TotalVisits">
            {isLoading ? (
                <LoaderContainer>
                    <LoadingSpinner />
                </LoaderContainer>
            ) : isNotEmpty ? (
                <div>
                    {showChange ? (
                        <POPChangeValueStyled>
                            <Change
                                value={changeFilter()(periodOverPeriodData.currPeriod.change)}
                                isDecrease={periodOverPeriodData.currPeriod.change < 0}
                            />
                            <Title>{translate("industryoverview.key.indicators.totalvisits.pop.title")}</Title>
                        </POPChangeValueStyled>
                    ) : null}

                    <SidesWrapper>
                        <LeftBarContainer>
                            <BarContainer>
                                <POPVisitsItemBar
                                    isCurrentPeriod={false}
                                    barHeight={periodOverPeriodData.comparedPeriod.barHeight}
                                    width={"48px"}
                                />
                            </BarContainer>
                            <DisplayValue>{valueProps.comparedPeriod.displayValue}</DisplayValue>
                            <Period>{metricNameProps.comparedPeriod.displayName}</Period>
                        </LeftBarContainer>
                        <RightBarContainer>
                            <BarContainer>
                                <POPVisitsItemBar
                                    isCurrentPeriod
                                    barHeight={periodOverPeriodData.currPeriod.barHeight}
                                    width={"48px"}
                                />
                            </BarContainer>
                            <DisplayValue>{valueProps.currPeriod.displayValue}</DisplayValue>
                            <Period>{metricNameProps.currPeriod.displayName}</Period>
                        </RightBarContainer>
                    </SidesWrapper>
                </div>
            ) : (
                <NoDataContainer>
                    <NoData
                        noDataTitleKey="global.nodata.notavilable"
                        noDataSubTitleKey="workspaces.marketing.nodata.subtitle"
                    />
                </NoDataContainer>
            )}
        </TotalVisitsWrapper>
    );
};

export default TotalVisits;
